import { doDefaultFetch } from '@nhl/fetch-helpers';
import { constants } from '@nhl/formatter-utilities';
import { runFunctionOrGetValue } from '@nhl/generic-collection-utils';

import gameStateTypes from '../utils/gameStateTypes';

const IS_ROOKIE_PLAYER_STATS = 'IS_ROOKIE_PLAYER_STATS';
const IS_ROOKIE_GOALIE_STATS = 'IS_ROOKIE_GOALIE_STATS';
const IS_ROOKIE_STATS = 'IS_ROOKIE_STATS';

const fetchPlayerCountries = args => {
    return doDefaultFetch(`/site/api/player/countries`, args);
};

const fetchFranchises = args => {
    const sortJson = [{
        property: 'teamPlaceName',
        direction: 'ASC',
    }, {
        property: 'teamCommonName',
        direction: 'ASC',
    }];

    const includes = [{
        franchiseTeams: [
            {
                team: [...teamIncludes],
            },
            'firstSeason.id',
            'lastSeason.id',
        ],
    }];

    return doDefaultFetch(`/site/api/franchises?sort=${JSON.stringify(sortJson)}`
        + `&include=${JSON.stringify(includes)}`, args);
};

const fetchSkaterSummaryReport = args => {
    return doDefaultFetch(`/site/api/player/${statsOrTotals(args)}` +
        `?cayenneExp=${generateCayenneExp(args)}` +
        '&sort=[{"property": "points", "direction": "DESC"}, {"property": "goals", "direction": "DESC"}, {"property": "gamesPlayed", "direction": "ASC"}]' +
        getPlayerTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=gamesPlayed' +
        '&include=goals' +
        '&include=assists' +
        '&include=points' +
        '&include=plusMinus' +
        '&include=penaltyMinutes' +
        '&include=powerplayGoals' +
        '&include=shorthandedGoals' +
        '&include=gameWinningGoals' +
        '&include=overtimeGoals' +
        '&include=shots' +
        '&include=shootingPctg' +
        '&include=avgTimeOnIcePerGame' +
        '&include=avgShiftsPerGame' +
        '&include=totalFaceoffs' +
        '&include=faceoffWinPctg',
        args
    )
};

const fetchSkaterAssistsReport = args => {
    return doDefaultFetch(`/site/api/player/${statsOrTotals(args)}` +
        `?cayenneExp=${generateCayenneExp(args)}` +
        '&sort=[{"property": "assists", "direction": "DESC"}, {"property": "points", "direction": "DESC"}, {"property": "gamesPlayed", "direction": "ASC"}]' +
        getPlayerTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=gamesPlayed' +
        '&include=goals' +
        '&include=assists' +
        '&include=points' +
        '&include=plusMinus' +
        '&include=evenStrengthAssists' +
        '&include=shorthandedAssists' +
        '&include=powerplayAssists' +
        '&include=homeAssists' +
        '&include=roadAssists' +
        '&include=ownDivAssists' +
        '&include=otherDivAssists' +
        '&include=avgAssistsPerGame',
        args
    );
};

const fetchSkaterBiosReport = args => {
    return doDefaultFetch(`/site/api/player/${statsOrTotals(args)}` +
        `?cayenneExp=${generateCayenneExp(args)}` +
        getPlayerTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=player.birthDate' +
        '&include=player.birthCity' +
        '&include=player.birthStateProvince' +
        '&include=player.birthCountry' +
        '&include=player.height' +
        '&include=player.weight' +
        '&include=player.shootsCatches' +
        '&include=teamPlayerBio.sweaterNumber' +
        '&include=isRookie' +
        '&include=gamesPlayed' +
        '&include=goals' +
        '&include=assists' +
        '&include=points' +
        '&include=plusMinus' +
        '&include=penaltyMinutes' +
        '&include=avgTimeOnIcePerGame',
        args
    );
};

const fetchSkaterDivisionReport = args => {
    return doDefaultFetch(`/site/api/player/${statsOrTotals(args)}` +
        `?cayenneExp=${generateCayenneExp(args)}` +
        '&sort=[{"property": "ownDivPoints", "direction": "DESC"}, {"property": "ownDivGoals", "direction": "DESC"}]' +
        getPlayerTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=ownDivGamesPlayed' +
        '&include=ownDivGoals' +
        '&include=ownDivAssists' +
        '&include=ownDivPoints' +
        '&include=ownDivPlusMinus' +
        '&include=otherDivGamesPlayed' +
        '&include=otherDivGoals' +
        '&include=otherDivAssists' +
        '&include=otherDivPoints' +
        '&include=otherDivPlusMinus' +
        '&include=gamesPlayed' +
        '&include=goals' +
        '&include=assists' +
        '&include=points&include=plusMinus',
        args
    );
};

const fetchSkaterFaceoffLeadersReport = args => {
    return doDefaultFetch(`/site/api/faceoffLeaders` +
        `?cayenneExp=${generateCayenneExp(args, false, IS_ROOKIE_STATS)}` +
        '&sort=[{"property": "faceoffWinPctg", "direction": "DESC"}]' +
        getPlayerTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=gamesPlayed' +
        '&include=evenStrengthFaceoffsWon' +
        '&include=evenStrengthFaceoffsLost' +
        '&include=powerplayFaceoffsWon' +
        '&include=powerplayFaceoffsLost' +
        '&include=shorthandedFaceoffsWon' +
        '&include=shorthandedFaceoffsLost' +
        '&include=homeFaceoffsWonTotal' +
        '&include=homeFaceoffsLostTotal' +
        '&include=homeFaceoffWinPctg' +
        '&include=roadFaceoffsWonTotal' +
        '&include=roadFaceoffsLostTotal' +
        '&include=roadFaceoffWinPctg' +
        '&include=faceoffsWon' +
        '&include=faceoffsLost' +
        '&include=totalFaceoffs' +
        '&include=faceoffWinPctg',
        args
    );
};

const fetchSkaterFaceoffsReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += possibleAnd(cayenneExp) + 'totalFaceoffs > 0';

    return doDefaultFetch(`/site/api/player/${statsOrTotals(args)}` +
        `?cayenneExp=${cayenneExp}` +
        '&sort=[{"property": "totalFaceoffs", "direction": "DESC"}]' +
        getPlayerTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=gamesPlayed' +
        '&include=evenStrengthFaceoffsWon' +
        '&include=evenStrengthFaceoffsLost' +
        '&include=powerplayFaceoffsWon' +
        '&include=powerplayFaceoffsLost' +
        '&include=shorthandedFaceoffsWon' +
        '&include=shorthandedFaceoffsLost' +
        '&include=homeFaceoffsWonTotal' +
        '&include=homeFaceoffsLostTotal' +
        '&include=homeFaceoffWinPctg' +
        '&include=roadFaceoffsWonTotal' +
        '&include=roadFaceoffsLostTotal' +
        '&include=roadFaceoffWinPctg' +
        '&include=faceoffsWon' +
        '&include=faceoffsLost' +
        '&include=totalFaceoffs' +
        '&include=faceoffWinPctg',
        args
    );
};

const fetchSkaterGoalsReport = args => {
    return doDefaultFetch(`/site/api/player/${statsOrTotals(args)}` +
        `?cayenneExp=${generateCayenneExp(args)}` +
        '&sort=[{"property": "goals", "direction": "DESC"}, {"property": "points", "direction": "DESC"}, {"property": "gamesPlayed", "direction": "ASC"}]' +
        getPlayerTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=gamesPlayed' +
        '&include=goals' +
        '&include=assists' +
        '&include=points' +
        '&include=plusMinus' +
        '&include=evenStrengthGoals' +
        '&include=powerplayGoals' +
        '&include=shorthandedGoals' +
        '&include=firstGoals' +
        '&include=overtimeGoals' +
        '&include=gameWinningGoals' +
        '&include=homeGoals' +
        '&include=roadGoals' +
        '&include=ownDivGoals' +
        '&include=otherDivGoals' +
        '&include=emptyNetGoals' +
        '&include=penaltyShotGoals' +
        '&include=penaltyShotsTaken' +
        '&include=avgGoalsPerGame',
        args
    );
};

const fetchSkaterHomeReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += possibleAnd(cayenneExp) + 'homeGamesPlayed > 0';

    return doDefaultFetch(`/site/api/player/${statsOrTotals(args)}` +
        `?cayenneExp=${cayenneExp}` +
        '&sort=[{"property": "homePoints", "direction": "DESC"}, {"property": "homeGoals", "direction": "DESC"}, {"property": "homeGamesPlayed", "direction": "ASC"}]' +
        getPlayerTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=homeGamesPlayed' +
        '&include=homeGoals' +
        '&include=homeAssists' +
        '&include=homePoints' +
        '&include=homePlusMinus' +
        '&include=homePenaltyMinutes' +
        '&include=homeAvgEvenStrengthTimeOnIcePerGame' +
        '&include=homeAvgPowerplayTimeOnIcePerGame' +
        '&include=homeAvgShorthandedTimeOnIcePerGame' +
        '&include=homeAvgTimeOnIcePerGame' +
        '&include=homeTotalFaceoffs' +
        '&include=homeFaceoffsWonTotal' +
        '&include=homeFaceoffsLostTotal' +
        '&include=homeFaceoffWinPctg',
        args
    );
};

const fetchSkaterPenaltiesReport = args => {
    return doDefaultFetch(`/site/api/player/${statsOrTotals(args)}` +
        `?cayenneExp=${generateCayenneExp(args)}` +
        '&sort=[{"property": "penaltyMinutes", "direction": "DESC"}]' +
        getPlayerTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=gamesPlayed' +
        '&include=penaltyMinutes' +
        '&include=minorPenalties' +
        '&include=majorPenalties' +
        '&include=misconductPenalties' +
        '&include=gameMisconductPenalties' +
        '&include=matchPenalties' +
        '&include=penalties',
        args
    );
};

const fetchSkaterPlusMinusReport = args => {
    return doDefaultFetch(`/site/api/player/${statsOrTotals(args)}` +
        `?cayenneExp=${generateCayenneExp(args)}` +
        '&sort=[{"property": "plusMinus", "direction": "DESC"}, {"property": "points", "direction": "DESC"}, {"property": "gamesPlayed", "direction": "ASC"}]' +
        getPlayerTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=gamesPlayed' +
        '&include=goals' +
        '&include=assists' +
        '&include=points' +
        '&include=plusMinus' +
        '&include=homePlusMinus' +
        '&include=roadPlusMinus' +
        '&include=ownDivPlusMinus' +
        '&include=otherDivPlusMinus',
        args
    );
};

const fetchSkaterPointsReport = args => {
    return doDefaultFetch(`/site/api/player/${statsOrTotals(args)}` +
        `?cayenneExp=${generateCayenneExp(args)}` +
        '&sort=[{"property": "points", "direction": "DESC"}, {"property": "goals", "direction": "DESC"}, {"property": "gamesPlayed", "direction": "ASC"}]' +
        getPlayerTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=gamesPlayed' +
        '&include=goals' +
        '&include=assists' +
        '&include=points' +
        '&include=plusMinus' +
        '&include=evenStrengthPoints' +
        '&include=shorthandedPoints' +
        '&include=powerplayPoints' +
        '&include=homePoints' +
        '&include=roadPoints' +
        '&include=ownDivPoints' +
        '&include=otherDivPoints' +
        '&include=avgPointsPerGame',
        args
    );
};

const fetchSkaterRealTimeReport = args => {
    return doDefaultFetch(`/site/api/player/${statsOrTotals(args)}` +
        `?cayenneExp=${generateCayenneExp(args)}` +
        '&sort=[{"property": "hits", "direction": "DESC"}]' +
        getPlayerTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=gamesPlayed' +
        '&include=hits' +
        '&include=blockedShots' +
        '&include=missedShots' +
        '&include=giveaways' +
        '&include=takeaways' +
        '&include=faceoffsWon' +
        '&include=faceoffsLost' +
        '&include=totalFaceoffs' +
        '&include=faceoffWinPctg' +
        '&include=faceoffPctg' +
        '&include=shots' +
        '&include=goals' +
        '&include=shootingPctg',
        args
    );
};

const fetchSkaterRoadReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += possibleAnd(cayenneExp) + 'roadGamesPlayed > 0';

    return doDefaultFetch(`/site/api/player/${statsOrTotals(args)}` +
        `?cayenneExp=${cayenneExp}` +
        '&sort=[{"property": "roadPoints", "direction": "DESC"}, {"property": "roadGoals", "direction": "DESC"}, {"property": "roadGamesPlayed", "direction": "ASC"}]' +
        getPlayerTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=roadGamesPlayed' +
        '&include=roadGoals' +
        '&include=roadAssists' +
        '&include=roadPoints' +
        '&include=roadPlusMinus' +
        '&include=roadPenaltyMinutes' +
        '&include=roadAvgEvenStrengthTimeOnIcePerGame' +
        '&include=roadAvgPowerplayTimeOnIcePerGame' +
        '&include=roadAvgShorthandedTimeOnIcePerGame' +
        '&include=roadAvgTimeOnIcePerGame' +
        '&include=roadTotalFaceoffs' +
        '&include=roadFaceoffsWonTotal' +
        '&include=roadFaceoffsLostTotal' +
        '&include=roadFaceoffWinPctg',
        args
    );
};

const fetchSkaterShootingPercentageLeadersReport = args => {
    return doDefaultFetch(`/site/api/shootingPctgLeaders` +
        `?cayenneExp=${generateCayenneExp(args)}` +
        '&sort=[{"property": "shootingPercentage", "direction": "DESC"}, {"property": "goals", "direction": "DESC"}]' +
        getPlayerTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=gamesPlayed' +
        '&include=shots' +
        '&include=goals' +
        '&include=shootingPercentage' +
        '&include=avgShotsPerGame' +
        '&include=penaltyShots' +
        '&include=penaltyShotsTaken',
        args
    );
};

const fetchSkaterShootingPercentageReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += possibleAnd(cayenneExp) + 'shots > 0';

    return doDefaultFetch(`/site/api/player/${statsOrTotals(args)}` +
        `?cayenneExp=${cayenneExp}` +
        '&sort=[{"property": "shootingPctg", "direction": "DESC"}]' +
        getPlayerTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=gamesPlayed' +
        '&include=shots' +
        '&include=goals' +
        '&include=shootingPctg' +
        '&include=avgShotsPerGame' +
        '&include=penaltyShotGoals' +
        '&include=penaltyShotsTaken',
        args
    );
};

const fetchSkaterShootoutReport = args => {
    return doDefaultFetch(`/site/api/player/shootout${summaryOrTotals(args)}` +
        `?cayenneExp=${generateCayenneExp(args, false, IS_ROOKIE_STATS)}` +
        '&sort=[{"property": "goals", "direction": "DESC"}]' +
        getPlayerTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=homeShotsTaken' +
        '&include=homeGoals' +
        '&include=homeShootingPctg' +
        '&include=homeDecidingGoals' +
        '&include=roadShotsTaken' +
        '&include=roadGoals' +
        '&include=roadShootingPctg' +
        '&include=roadDecidingGoals' +
        '&include=shotsTaken' +
        '&include=goals' +
        '&include=shootingPctg' +
        '&include=gameDecidingGoals',
        args
    );
};

const fetchSkaterPointsByStrengthReport = args => {
    return doDefaultFetch(`/site/api/player/${statsOrTotals(args)}` +
        `?cayenneExp=${generateCayenneExp(args)}` +
        '&sort=[{"property": "evenStrengthPoints", "direction": "DESC"}]' +
        getPlayerTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=gamesPlayed' +
        '&include=evenStrengthGoals' +
        '&include=evenStrengthAssists' +
        '&include=evenStrengthPoints' +
        '&include=powerplayGoals' +
        '&include=powerplayAssists' +
        '&include=powerplayPoints' +
        '&include=shorthandedGoals' +
        '&include=shorthandedAssists' +
        '&include=shorthandedPoints' +
        '&include=goals' +
        '&include=assists' +
        '&include=points',
        args
    );
};

const fetchSkaterTimeOnIceReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += possibleAnd(cayenneExp) + 'gamesPlayed > 0';

    return doDefaultFetch(`/site/api/player/${statsOrTotals(args)}` +
        `?cayenneExp=${cayenneExp}` +
        '&sort=[{"property": "timeOnIce", "direction": "DESC"}]' +
        getPlayerTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=gamesPlayed' +
        '&include=evenStrengthTimeOnIce' +
        '&include=avgEvenStrengthTOIPerGame' +
        '&include=shorthandedTimeOnIce' +
        '&include=avgShorthandedTOIPerGame' +
        '&include=powerplayTimeOnIce' +
        '&include=avgPowerplayTOIPerGame' +
        '&include=timeOnIce' +
        '&include=avgTimeOnIcePerGame' +
        '&include=shifts' +
        '&include=avgTimeOnIcePerShift' +
        '&include=avgShiftsPerGame',
        args
    );
};

const fetchGoalieSummaryReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}gamesPlayed > 0`;

    return doDefaultFetch(`/site/api/goalie/${statsOrTotals(args)}` +
        `?cayenneExp=${cayenneExp}` +
        '&sort=[{"property": "wins", "direction": "DESC"}, {"property": "gamesPlayed"}]' +
        getGoalieTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=gamesPlayed' +
        '&include=gamesStarted' +
        '&include=wins' +
        '&include=losses' +
        '&include=ties' +
        '&include=overtimeLosses' +
        '&include=shotsAgainst' +
        '&include=goalsAgainst' +
        '&include=goalsAgainstAverage' +
        '&include=saves' +
        '&include=savePercentage' +
        '&include=shutouts' +
        '&include=goals' +
        '&include=assists' +
        '&include=points' +
        '&include=penaltyMinutes' +
        '&include=timeOnIce',
        args
    );
};

const fetchGoalieBiosReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}gamesPlayed > 0`;

    return doDefaultFetch(`/site/api/goalie/${statsOrTotals(args)}` +
        `?cayenneExp=${cayenneExp}` +
        getGoalieTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=player.birthDate' +
        '&include=player.birthCity' +
        '&include=player.birthStateProvince' +
        '&include=player.birthCountry' +
        '&include=player.height' +
        '&include=player.height' +
        '&include=player.weight' +
        '&include=player.shootsCatches' +
        '&include=teamPlayerBio.sweaterNumber' +
        '&include=isRookie' +
        '&include=gamesPlayed' +
        '&include=wins' +
        '&include=losses' +
        '&include=ties' +
        '&include=overtimeLosses' +
        '&include=goalsAgainstAverage' +
        '&include=savePercentage' +
        '&include=shutouts',
        args
    );
};

const fetchGoalieGoalsAgainstAverageLeadersReport = args => {
    return doDefaultFetch(`/site/api/goalie/gaaLeaders` +
        `?cayenneExp=${generateCayenneExp(args, false, IS_ROOKIE_STATS)}` +
        '&sort=[{"property": "goalsAgainstAverage", "direction": "ASC"}]' +
        getGoalieTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=gamesPlayed' +
        '&include=gamesStarted' +
        '&include=shotsAgainst' +
        '&include=goalsAgainst' +
        '&include=goalsAgainstAverage' +
        '&include=wins' +
        '&include=losses' +
        '&include=ties' +
        '&include=overtimeLosses' +
        '&include=saves' +
        '&include=savePercentage' +
        '&include=shutouts' +
        '&include=goals' +
        '&include=assists' +
        '&include=points' +
        '&include=penaltyMinutes' +
        '&include=timeOnIce',
        args
    );
};

const fetchGoalieGoalsAgainstAverageReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}gamesPlayed > 0`;

    return doDefaultFetch(`/site/api/goalie/${statsOrTotals(args)}` +
        `?cayenneExp=${cayenneExp}` +
        '&sort=[{"property": "goalsAgainstAverage", "direction": "ASC"}]' +
        getGoalieTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=gamesPlayed' +
        '&include=gamesStarted' +
        '&include=shotsAgainst' +
        '&include=goalsAgainst' +
        '&include=goalsAgainstAverage' +
        '&include=wins' +
        '&include=losses' +
        '&include=ties' +
        '&include=overtimeLosses' +
        '&include=saves' +
        '&include=savePercentage' +
        '&include=shutouts' +
        '&include=goals' +
        '&include=assists' +
        '&include=points' +
        '&include=penaltyMinutes' +
        '&include=timeOnIce',
        args
    );
};

const fetchGoalieHomeReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}homeGamesPlayed > 0`;

    return doDefaultFetch(`/site/api/goalie/${statsOrTotals(args)}` +
        `?cayenneExp=${cayenneExp}` +
        '&sort=[{"property": "wins", "direction": "DESC"}, {"property": "gamesPlayed"}]' +
        getGoalieTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=homeGamesPlayed' +
        '&include=homeWins' +
        '&include=homeLosses' +
        '&include=homeTies' +
        '&include=homeOvertimeLosses' +
        '&include=homeShotsAgainst' +
        '&include=homeGoalsAgainst' +
        '&include=homeGoalsAgainstAverage' +
        '&include=homeSavePercentage' +
        '&include=homeShutouts' +
        '&include=homeMinutesPlayed',
        args
    );
};

const fetchGoaliePenaltyShotsReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}penaltyShotsAgainst > 0`;

    return doDefaultFetch(`/site/api/goalie/${statsOrTotals(args)}` +
        `?cayenneExp=${cayenneExp}` +
        '&sort=[{"property": "penaltyShotsAgainst", "direction": "DESC"}]' +
        getGoalieTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=gamesPlayed' +
        '&include=gamesStarted' +
        '&include=penaltyShotsAgainst' +
        '&include=penaltyShotsGoalsAgainst' +
        '&include=penaltyShotsSaves' +
        '&include=penaltyShotsSavePctg',
        args
    );
};

const fetchGoalieRoadReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}roadGamesPlayed > 0`;

    return doDefaultFetch(`/site/api/goalie/${statsOrTotals(args)}` +
        `?cayenneExp=${cayenneExp}` +
        '&sort=[{"property": "wins", "direction": "DESC"}, {"property": "gamesPlayed"}]' +
        getGoalieTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=roadGamesPlayed' +
        '&include=roadWins' +
        '&include=roadLosses' +
        '&include=roadTies' +
        '&include=roadOvertimeLosses' +
        '&include=roadShotsAgainst' +
        '&include=roadGoalsAgainst' +
        '&include=roadGoalsAgainstAverage' +
        '&include=roadSavePercentage' +
        '&include=roadShutouts' +
        '&include=roadMinutesPlayed',
        args
    );
};

const fetchGoalieSavePercentageLeadersReport = args => {
    return doDefaultFetch(`/site/api/goalie/savePercentageLeaders` +
        `?cayenneExp=${generateCayenneExp(args, false, IS_ROOKIE_STATS)}` +
        '&sort=[{"property": "savePercentage", "direction": "DESC"}]' +
        getGoalieTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=gamesPlayed' +
        '&include=gamesStarted' +
        '&include=saves' +
        '&include=savePercentage' +
        '&include=wins' +
        '&include=losses' +
        '&include=ties' +
        '&include=overtimeLosses' +
        '&include=goalsAgainst' +
        '&include=goalsAgainstAverage' +
        '&include=shotsAgainst' +
        '&include=shutouts' +
        '&include=goals' +
        '&include=assists' +
        '&include=points' +
        '&include=penaltyMinutes' +
        '&include=timeOnIce',
        args
    );
};

const fetchGoalieSavePercentageReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}gamesPlayed > 0`;

    return doDefaultFetch(`/site/api/goalie/${statsOrTotals(args)}` +
        `?cayenneExp=${cayenneExp}` +
        '&sort=[{"property": "savePercentage", "direction": "DESC"}]' +
        getGoalieTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=gamesPlayed' +
        '&include=gamesStarted' +
        '&include=saves' +
        '&include=savePercentage' +
        '&include=wins' +
        '&include=losses' +
        '&include=ties' +
        '&include=overtimeLosses' +
        '&include=goalsAgainst' +
        '&include=goalsAgainstAverage' +
        '&include=shotsAgainst' +
        '&include=shutouts' +
        '&include=goals' +
        '&include=assists' +
        '&include=points' +
        '&include=penaltyMinutes' +
        '&include=timeOnIce',
        args
    );
};

const fetchGoalieShootoutsReport = args => {
    let cayenneExp = generateCayenneExp(args, false, IS_ROOKIE_STATS);
    cayenneExp += `${possibleAnd(cayenneExp)}shootoutGamesPlayed > 0`;

    return doDefaultFetch(`/site/api/goalie/shootout${summaryOrTotals(args)}` +
        `?cayenneExp=${cayenneExp}` +
        '&sort=[{"property": "shootoutGamesWon", "direction": "DESC"}]' +
        getGoalieTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=homeShootoutGamesPlayed' +
        '&include=homeShootoutGamesWon' +
        '&include=homeShootoutGamesLost' +
        '&include=homeShotsAgainst' +
        '&include=homeGoalsAllowed' +
        '&include=homeSavePctg' +
        '&include=roadShootoutGamesPlayed' +
        '&include=roadShootoutGamesWon' +
        '&include=roadShootoutGamesLost' +
        '&include=roadShotsAgainst' +
        '&include=roadGoalsAllowed' +
        '&include=roadSavePctg' +
        '&include=shootoutGamesPlayed' +
        '&include=shootoutGamesWon' +
        '&include=shootoutGamesLost' +
        '&include=shotsAgainst' +
        '&include=goalsAllowed' +
        '&include=savePctg',
        args
    );
};

const fetchGoalieSpecialTeamsReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}gamesPlayed > 0`;

    return doDefaultFetch(`/site/api/goalie/${statsOrTotals(args)}` +
        `?cayenneExp=${cayenneExp}` +
        '&sort=[{"property": "evenStrengthSavePctg", "direction": "DESC"}]' +
        getGoalieTeamIncludes(args) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=gamesPlayed' +
        '&include=gamesStarted' +
        '&include=evenStrengthShotsAgainst' +
        '&include=evenStrengthGoalsAgainst' +
        '&include=evenStrengthSaves' +
        '&include=evenStrengthSavePctg' +
        '&include=powerplayShotsAgainst' +
        '&include=powerplayGoalsAgainst' +
        '&include=powerplaySaves' +
        '&include=powerplaySavePctg' +
        '&include=shorthandedShotsAgainst' +
        '&include=shorthandedGoalsAgainst' +
        '&include=shorthandedSaves' +
        '&include=shorthandedSavePctg' +
        '&include=shotsAgainst' +
        '&include=goalsAgainst' +
        '&include=saves' +
        '&include=savePercentage',
        args
    );
};

const fetchSkaterCareerCareerLeadersReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}gamesPlayed > 0`;

    return doDefaultFetch('/site/api/player/careerTotals' +
        `?cayenneExp=${cayenneExp}` +
        getSortExpression(args.sortInfo, 'player.currentTeam') +
        '&include=player.currentTeam.triCode' +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=firstSeasonForGameType.id' +
        '&include=lastSeasonForGameType.id' +
        '&include=gamesPlayed' +
        '&include=goals' +
        '&include=assists' +
        '&include=points' +
        '&include=plusMinus' +
        '&include=penaltyMinutes' +
        '&include=powerplayGoals' +
        '&include=shorthandedGoals' +
        '&include=gameWinningGoals' +
        '&include=gameTyingGoals' +
        '&include=overtimeGoals' +
        '&include=shots' +
        getPaginationExpression(args.start, args.limit),
        args
    );
};

const fetchSkaterCareerCareerLeadersForFranchiseReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}gamesPlayed > 0`;

    const includeJson = [
        {
            player: [
                ...playerIncludes,
                'position.positionCode',
            ],
        },
        {
            franchise: [
                {
                    franchiseTeams: [
                        {
                            team: [...teamIncludes],
                        },
                        'firstSeason.id',
                        'lastSeason.id',
                    ],
                },
                'mostRecentTeam.triCode',
            ],
        },
        'firstSeasonForGameType.id',
        'lastSeasonForGameType.id',
        'gamesPlayed',
        'goals',
        'assists',
        'points',
        'plusMinus',
        'penaltyMinutes',
        'powerplayGoals',
        'shorthandedGoals',
        'gameWinningGoals',
        'gameTyingGoals',
        'overtimeGoals',
        'shots',
    ];

    return doDefaultFetch('/site/api/player/careerTotalsByFranchise' +
        `?cayenneExp=${cayenneExp}` +
        getSortExpression(args.sortInfo) +
        `&include=${JSON.stringify(includeJson)}` +
        getPaginationExpression(args.start, args.limit),
        args
    );
};

const fetchSkaterCareerSingleSeasonLeadersReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}gamesPlayed > 0`;

    return doDefaultFetch('/site/api/player/totals' +
        `?cayenneExp=${cayenneExp}` +
        getSortExpression(args.sortInfo) +
        '&include=playerStats.team.triCode' +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=season.id' +
        '&include=gamesPlayed' +
        '&include=goals' +
        '&include=assists' +
        '&include=points' +
        '&include=plusMinus' +
        '&include=penaltyMinutes' +
        '&include=powerplayGoals' +
        '&include=shorthandedGoals' +
        '&include=gameWinningGoals' +
        '&include=gameTyingGoals' +
        '&include=overtimeGoals' +
        '&include=shots' +
        getPaginationExpression(args.start, args.limit),
        args
    );
};

const fetchSkaterCareerSingleSeasonLeadersForFranchiseReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}gamesPlayed > 0`;

    return doDefaultFetch('/site/api/player/stats' +
        `?cayenneExp=${cayenneExp}` +
        getSortExpression(args.sortInfo) +
        '&include=team.triCode' +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=season.id' +
        '&include=gamesPlayed' +
        '&include=goals' +
        '&include=assists' +
        '&include=points' +
        '&include=plusMinus' +
        '&include=penaltyMinutes' +
        '&include=powerplayGoals' +
        '&include=shorthandedGoals' +
        '&include=gameWinningGoals' +
        '&include=gameTyingGoals' +
        '&include=overtimeGoals' +
        '&include=shots' +
        getPaginationExpression(args.start, args.limit),
        args
    );
};

const fetchSkaterCareerCareerPenaltyShotsReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}penaltyShotsTaken > 0`;

    return doDefaultFetch('/site/api/player/careerTotals' +
        `?cayenneExp=${cayenneExp}` +
        getSortExpression(args.sortInfo) +
        '&include=player.currentTeam.triCode' +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=penaltyShotsTaken' +
        '&include=penaltyShots' +
        '&include=penaltyShotShootingPctg' +
        getPaginationExpression(args.start, args.limit),
        args
    );
};

const fetchSkaterCareerCareerShootoutLeadersReport = args => {
    return doDefaultFetch(`/site/api/player/careerShootoutTotals` +
        `?cayenneExp=${generateCayenneExp(args)}` +
        getSortExpression(args.sortInfo) +
        '&include=player.currentTeam.triCode' +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=homeShotsTaken' +
        '&include=homeGoals' +
        '&include=homeShootingPctg' +
        '&include=homeDecidingGoals' +
        '&include=roadShotsTaken' +
        '&include=roadGoals' +
        '&include=roadShootingPctg' +
        '&include=roadDecidingGoals' +
        '&include=shotsTaken' +
        '&include=goals' +
        '&include=shootingPctg' +
        '&include=gameDecidingGoals' +
        getPaginationExpression(args.start, args.limit),
        args
    );
};

const fetchSkaterCareerCareerOvertimeReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}overtimeGamesPlayed > 0`;

    return doDefaultFetch('/site/api/player/careerTotals' +
        `?cayenneExp=${cayenneExp}` +
        getSortExpression(args.sortInfo) +
        '&include=player.currentTeam.triCode' +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=overtimeGamesPlayed' +
        '&include=overtimeGoals' +
        '&include=overtimeAssists' +
        '&include=overtimePoints' +
        getPaginationExpression(args.start, args.limit),
        args
    );
};

const fetchTeamScoringByDefensemenReport = args => {
    return doDefaultFetch('/site/api/team/defensemanScoringTotals' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=gamesPlayed' +
        '&include=defensemanGoals' +
        '&include=defensemanAssists' +
        '&include=defensemanPoints' +
        '&include=defensemanGoalPercentage' +
        '&include=defensemanAssistPercentage' +
        '&include=defensemanPointPercentage' +
        '&include=defensemanPowerplayGoals' +
        '&include=defensemanShorthandedGoals' +
        '&include=defensemanGameWinningGoals' +
        '&include=totalGoals' +
        '&include=totalAssists' +
        '&include=totalPoints',
        args
    );
};

const fetchTeamScoringByForwardsReport = args => {
    return doDefaultFetch('/site/api/team/defensemanScoringTotals' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=gamesPlayed' +
        '&include=forwardGoals' +
        '&include=forwardAssists' +
        '&include=forwardPoints' +
        '&include=forwardGoalPercentage' +
        '&include=forwardAssistPercentage' +
        '&include=forwardPointPercentage' +
        '&include=forwardPowerplayGoals' +
        '&include=forwardShorthandedGoals' +
        '&include=forwardGameWinningGoals' +
        '&include=totalGoals' +
        '&include=totalAssists' +
        '&include=totalPoints',
        args
    );
};

const fetchTeamScoringByGoaliesReport = args => {
    return doDefaultFetch('/site/api/team/defensemanScoringTotals' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=gamesPlayed' +
        '&include=goalieGoals' +
        '&include=goalieAssists' +
        '&include=goaliePoints' +
        '&include=totalGoals' +
        '&include=totalAssists' +
        '&include=totalPoints',
        args
    );
};

const fetchTeamScoringByRookiesReport = args => {
    return doDefaultFetch('/site/api/team/rookieScoringTotals' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=gamesPlayed' +
        '&include=rookieGoals' +
        '&include=rookieAssists' +
        '&include=rookiePoints' +
        '&include=rookieGoalPercentage' +
        '&include=rookieAssistPercentage' +
        '&include=rookiePointPercentage' +
        '&include=rookiePowerplayGoals' +
        '&include=rookieShorthandedGoals' +
        '&include=rookieGameWinningGoals' +
        '&include=totalGoals' +
        '&include=totalAssists' +
        '&include=totalPoints',
        args
    );
};

const fetchTeamSavePercentageReport = args => {
    const additionalIncludes = [
        'gamesPlayed',
        'shotsAgainst',
        'saves',
        'goalsAgainst',
        'savePctg',
        'wins',
        'losses',
        'ties',
        'overtimeLosses',
        'timeOnIce',
        'goalsAgainstAvg',
        'shutouts'
    ];

    return doDefaultFetch('/site/api/team/stats' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        `&include=${JSON.stringify([{team: teamIncludes}, ...additionalIncludes])}`,
        args
    );
}

const fetchTeamSummaryReport = args => {
    const gameType = {
        value: args.gameType
    };
    return doDefaultFetch('/site/api/team/stats' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort(gameType))}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=leagueSequence' +
        '&include=gamesPlayed' +
        '&include=wins' +
        '&include=losses' +
        '&include=ties' +
        '&include=overtimeLosses' +
        '&include=regulationPlusOvertimeWins' +
        '&include=regulationWins' +
        '&include=points' +
        '&include=avgGoalsPerGame' +
        '&include=avgGoalsAgainstPerGame' +
        '&include=goals5on5ForAgainstRatio' +
        '&include=advantages' +
        '&include=powerplayPercentage' +
        '&include=totalPenalties' +
        '&include=penaltyKillPercentage' +
        '&include=avgShotsPerGame' +
        '&include=avgShotsAgainstPerGame' +
        '&include=avgShotsPerGame' +
        '&include=winPctgScoreFirst' +
        '&include=winPctgOppScoreFirst' +
        '&include=winPctgAfterLead1p' +
        '&include=winPctgAfterLead2p' +
        '&include=winPctgOutshootOpponent' +
        '&include=winPctgOutshotByOpponent' +
        '&include=faceoffs' +
        '&include=faceoffWinPercentage',
        args
    );
};

const fetchTeamComebackWinsReport = args => {
    const includeJson = [
        'season.id',
        'team.id',
        'team.placeName',
        'team.commonName',
        'team.triCode',
        'team.logos',
        'team.fullName',
    ];

    return doDefaultFetch('/site/api/team_comeback_wins' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&include=${JSON.stringify(includeJson)}`,
        args
    );
};

const fetchTeamTimeLeadingTrailingReport = (args) => {
    const includeJson = [
        'team.id',
        'team.placeName',
        'team.commonName',
        'team.triCode',
        'team.logos',
        'team.fullName',
    ];

    return doDefaultFetch('/site/api/team/time-leading-trailing' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&include=${JSON.stringify(includeJson)}`,
        args
    );
};

const fetchTeamGamesDecidedInOtSoReport = args => {
    return doDefaultFetch('/site/api/team/seasonOvertimeShootoutRecord' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        `&include=${JSON.stringify({team: [...teamIncludes]})}`,
        args
    );
};

const fetchTeamGoalsHomeRoadReport = args => {
    return doDefaultFetch('/site/api/team/stats' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=homeGamesPlayed' +
        '&include=homeGoals' +
        '&include=homeGoalsAgainst' +
        '&include=homePowerplayGoals' +
        '&include=homePowerplayGoalsAgainst' +
        '&include=homeShorthandedGoals' +
        '&include=homeShorthandedGoalsAgainst' +
        '&include=roadGamesPlayed' +
        '&include=roadGoals' +
        '&include=roadGoalsAgainst' +
        '&include=roadPowerplayGoals' +
        '&include=roadPowerplayGoalsAgainst' +
        '&include=roadShorthandedGoals' +
        '&include=roadShorthandedGoalsAgainst' +
        '&include=gamesPlayed' +
        '&include=goals' +
        '&include=goalsAgainst' +
        '&include=powerplayGoals' +
        '&include=powerplayGoalsAgainst' +
        '&include=shorthandedGoals' +
        '&include=shorthandedGoalsAgainst',
        args
    );
};

const fetchTeamGoalsByPeriodReport = args => {
    return doDefaultFetch('/site/api/team/stats' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=gamesPlayed' +
        '&include=avgGoalsPerGame' +
        '&include=avgGoalsAgainstPerGame' +
        '&include=goalsInPeriod1' +
        '&include=goalsInPeriod2' +
        '&include=goalsInPeriod3' +
        '&include=goalsInOT' +
        '&include=goals' +
        '&include=goalsAgainstInPeriod1' +
        '&include=goalsAgainstInPeriod2' +
        '&include=goalsAgainstInPeriod3' +
        '&include=goalsAgainstInOT' +
        '&include=goalsAgainst',
        args
    );
};

const fetchTeamGoalsForReport = args => {
    return doDefaultFetch('/site/api/team/stats' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=gamesPlayed' +
        '&include=goals5on5' +
        '&include=goals5on4' +
        '&include=goals5on3' +
        '&include=goals4on4' +
        '&include=goals4on3' +
        '&include=goals3on3' +
        '&include=goals3on4' +
        '&include=goals3on5' +
        '&include=goals3on6' +
        '&include=goals4on5' +
        '&include=goals4on6' +
        '&include=goals5on6' +
        '&include=goals6on5' +
        '&include=goals6on4' +
        '&include=goals6on3' +
        '&include=emptyNetGoals' +
        '&include=goalsByPenaltyShot' +
        '&include=goals' +
        '&include=avgGoalsPerGame',
        args
    );
};

const fetchTeamGoalsAgainstReport = args => {
    return doDefaultFetch('/site/api/team/stats' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=gamesPlayed' +
        '&include=goalsAgainst5on5' +
        '&include=goalsAgainst5on4' +
        '&include=goalsAgainst5on3' +
        '&include=goalsAgainst4on4' +
        '&include=goalsAgainst4on3' +
        '&include=goalsAgainst3on3' +
        '&include=goalsAgainst3on4' +
        '&include=goalsAgainst3on5' +
        '&include=goalsAgainst3on6' +
        '&include=goalsAgainst4on5' +
        '&include=goalsAgainst4on6' +
        '&include=goalsAgainst5on6' +
        '&include=goalsAgainst6on5' +
        '&include=goalsAgainst6on4' +
        '&include=goalsAgainst6on3' +
        '&include=emptyNetGoalsAgainst' +
        '&include=goalsAgainstByPenaltyShot' +
        '&include=goalsAgainst' +
        '&include=avgGoalsAgainstPerGame',
        args
    );
};

const fetchTeamGoalGamesReport = args => {
    return doDefaultFetch('/site/api/team/stats' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=gamesPlayed' +
        '&include=wins' +
        '&include=losses' +
        '&include=ties' +
        '&include=overtimeLosses' +
        '&include=wins1GoalGame' +
        '&include=loss1GoalGame' +
        '&include=otLoss1GoalGame' +
        '&include=winPctg1GoalGames' +
        '&include=wins2GoalGame' +
        '&include=loss2GoalGame' +
        '&include=winPctg2GoalGames' +
        '&include=wins3GoalGame' +
        '&include=loss3GoalGame' +
        '&include=winPctg3GoalGames' +
        '&include=shutouts' +
        '&include=timesShutout',
        args
    );
};

const fetchTeamHomeReport = args => {
    const gameType = {
        value: args.gameType
    };
    return doDefaultFetch('/site/api/team/stats' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort(gameType))}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=homeGamesPlayed' +
        '&include=homeWins' +
        '&include=homeLosses' +
        '&include=homeTies' +
        '&include=homeOTLosses' +
        '&include=homePoints' +
        '&include=homeGoals' +
        '&include=homeGoalsAgainst' +
        '&include=homePowerplayPercentage' +
        '&include=homePenaltyKillPercentage',
        args
    );
};

const fetchTeamRoadReport = args => {
    const gameType = {
        value: args.gameType
    };
    return doDefaultFetch('/site/api/team/stats' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort(gameType))}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=roadGamesPlayed' +
        '&include=roadWins' +
        '&include=roadLosses' +
        '&include=roadTies' +
        '&include=roadOTLosses' +
        '&include=roadPoints' +
        '&include=roadGoals' +
        '&include=roadGoalsAgainst' +
        '&include=roadPowerplayPercentage' +
        '&include=roadPenaltyKillPercentage',
        args
    );
};

const fetchTeamOutshootOutshotByReport = args => {
    return doDefaultFetch('/site/api/team/stats' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=gamesPlayed' +
        '&include=shots' +
        '&include=shotsAgainst' +
        '&include=avgShotsPerGame' +
        '&include=avgShotsAgainstPerGame' +
        '&include=winsOutshootOpponent' +
        '&include=lossOutshootOpponent' +
        '&include=tiesOutshootOpponent' +
        '&include=otLossOutshootOpponent' +
        '&include=winPctgOutshootOpponent' +
        '&include=winsOutshotByOpponent' +
        '&include=lossOutshotByOpponent' +
        '&include=tiesOutshotByOpponent' +
        '&include=otLossOutshotByOpponent' +
        '&include=winPctgOutshotByOpponent' +
        '&include=winsEvenShots' +
        '&include=lossEvenShots' +
        '&include=tiesEvenShots' +
        '&include=otLossEvenShots' +
        '&include=winPctgEvenShots',
        args
    );
};

const fetchTeamPenaltiesReport = args => {
    return doDefaultFetch('/site/api/team/stats' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=gamesPlayed' +
        '&include=minorPenalties' +
        '&include=majorPenalties' +
        '&include=misconductPenalties' +
        '&include=gameMisconducts' +
        '&include=matchPenalties' +
        '&include=totalPenalties' +
        '&include=penaltyMinutes' +
        '&include=benchPenalties' +
        '&include=benchPenaltyMinutes' +
        '&include=avgPenaltyMinutesPerGame',
        args
    );
};

const fetchTeamPowerplayReport = args => {
    return doDefaultFetch('/site/api/team/stats' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=homeGamesPlayed' +
        '&include=homeAdvantages' +
        '&include=homePowerplayGoals' +
        '&include=homePowerplayPercentage' +
        '&include=roadGamesPlayed' +
        '&include=roadAdvantages' +
        '&include=roadPowerplayGoals' +
        '&include=roadPowerplayPercentage' +
        '&include=gamesPlayed' +
        '&include=advantages' +
        '&include=powerplayGoals' +
        '&include=powerplayPercentage' +
        '&include=shorthandedGoalsAgainst',
        args
    );
};

const fetchTeamPowerplayTimeReport = args => {
    return doDefaultFetch('/site/api/team/stats' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=gamesPlayed' +
        '&include=advantages' +
        '&include=powerplayShots' +
        '&include=powerplayGoals' +
        '&include=powerplayPercentage' +
        '&include=teamPenaltyDetail.powerplaySeconds' +
        '&include=teamPenaltyDetail.times5On4' +
        '&include=teamPenaltyDetail.seconds5On4' +
        '&include=teamPenaltyDetail.goals5On4' +
        '&include=teamPenaltyDetail.times5On3' +
        '&include=teamPenaltyDetail.seconds5On3' +
        '&include=teamPenaltyDetail.goals5On3' +
        '&include=teamPenaltyDetail.times4On3' +
        '&include=teamPenaltyDetail.seconds4On3' +
        '&include=teamPenaltyDetail.goals4On3' +
        '&include=teamPenaltyDetail.times5On4' +
        '&include=teamPenaltyDetail.seconds5On4' +
        '&include=teamPenaltyDetail.goals5On4' +
        '&include=teamPenaltyDetail.period1TeamAdvantages' +
        '&include=teamPenaltyDetail.period2TeamAdvantages' +
        '&include=teamPenaltyDetail.period3TeamAdvantages' +
        '&include=teamPenaltyDetail.overtimeTeamAdvantages',
        args
    );
};

const fetchTeamPenaltyKillReport = args => {
    return doDefaultFetch('/site/api/team/stats' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=homeGamesPlayed' +
        '&include=homeTimesShorthanded' +
        '&include=homePowerplayGoalsAgainst' +
        '&include=homePenaltyKillPercentage' +
        '&include=roadGamesPlayed' +
        '&include=roadTimesShorthanded' +
        '&include=roadPowerplayGoalsAgainst' +
        '&include=roadPenaltyKillPercentage' +
        '&include=gamesPlayed' +
        '&include=timesShorthanded' +
        '&include=powerplayGoalsAgainst' +
        '&include=penaltyKillPercentage' +
        '&include=shorthandedGoals',
        args
    );
};

const fetchTeamPenaltyKillTimeReport = args => {
    return doDefaultFetch('/site/api/team/stats' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=gamesPlayed' +
        '&include=teamPenaltyDetail.homePowerplaySeconds' +
        '&include=teamPenaltyDetail.homePenaltyKillSeconds' +
        '&include=teamPenaltyDetail.homePowerplayMinusPenaltyKillSeconds' +
        '&include=teamPenaltyDetail.roadPowerplaySeconds' +
        '&include=teamPenaltyDetail.roadPenaltyKillSeconds' +
        '&include=teamPenaltyDetail.roadPowerplayMinusPenaltyKillSeconds' +
        '&include=teamPenaltyDetail.powerplaySeconds' +
        '&include=teamPenaltyDetail.penaltyKillSeconds' +
        '&include=teamPenaltyDetail.powerplayMinusPenaltyKillSeconds',
        args
    );
};

const fetchTeamPlusMinusReport = args => {
    return doDefaultFetch('/site/api/team/stats' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=homeGoals' +
        '&include=homeGoalsAgainst' +
        '&include=homePowerplayGoals' +
        '&include=homePowerplayGoalsAgainst' +
        '&include=roadGoals' +
        '&include=roadGoalsAgainst' +
        '&include=roadPowerplayGoals' +
        '&include=roadPowerplayGoalsAgainst' +
        '&include=goals' +
        '&include=goalsAgainst' +
        '&include=powerplayGoals' +
        '&include=powerplayGoalsAgainst' +
        '&include=plusMinus',
        args
    );
};

const fetchTeamRealTimeReport = args => {
    return doDefaultFetch('/site/api/team/stats' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=gamesPlayed' +
        '&include=homeHits' +
        '&include=homeBlockedShots' +
        '&include=homeMissedShots' +
        '&include=homeGiveaways' +
        '&include=homeTakeaways' +
        '&include=roadHits' +
        '&include=roadBlockedShots' +
        '&include=roadMissedShots' +
        '&include=roadGiveaways' +
        '&include=roadTakeaways' +
        '&include=hits' +
        '&include=blockedShots' +
        '&include=missedShots' +
        '&include=giveaways' +
        '&include=takeaways',
        args
    );
};

const fetchTeamRecordsInBackToBackGamesReport = args => {
    return doDefaultFetch('/site/api/team/seasonBackToBackRecord' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        `&include=${JSON.stringify({team: [...teamIncludes]})}`,
        args
    );
};

const fetchTeamLeadingTrailingReport = args => {
    return doDefaultFetch('/site/api/team/stats' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=gamesPlayed' +
        '&include=winsAfterLead1p' +
        '&include=lossAfterLead1p' +
        '&include=otLossAfterLead1p' +
        '&include=tiesAfterLead1p' +
        '&include=winsAfterLead2p' +
        '&include=lossAfterLead2p' +
        '&include=tiesAfterLead2p' +
        '&include=otLossAfterLead2p' +
        '&include=winsAfterTrail1p' +
        '&include=lossAfterTrail1p' +
        '&include=tiesAfterTrail1p' +
        '&include=otLossAfterTrail1p' +
        '&include=winsAfterTrail2p' +
        '&include=lossAfterTrail2p' +
        '&include=tiesAfterTrail2p' +
        '&include=otLossAfterTrail2p' +
        '&include=winsAfterTied1p' +
        '&include=lossAfterTied1p' +
        '&include=tiesAfterTied1p' +
        '&include=otLossAfterTied1p' +
        '&include=winsAfterTied2p' +
        '&include=lossAfterTied2p' +
        '&include=tiesAfterTied2p' +
        '&include=otLossAfterTied2p',
        args
    );
};

const fetchTeamScoreTrailFirstReport = args => {
    return doDefaultFetch('/site/api/team/stats' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${generateTeamSort(args.defaultSort)}` +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=gamesPlayed' +
        '&include=winsScoringFirst' +
        '&include=lossScoringFirst' +
        '&include=otLossScoringFirst' +
        '&include=tiesScoringFirst' +
        '&include=winsOpponentScoreFirst' +
        '&include=lossOpponentScoreFirst' +
        '&include=tiesOpponentScoreFirst' +
        '&include=otLossOpponentScoreFirst' +
        '&include=wins' +
        '&include=losses' +
        '&include=ties' +
        '&include=overtimeLosses',
        args
    );
};

const fetchTeamShotsByPeriodReport = args => {
    return doDefaultFetch('/site/api/team/shotDetails' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        '&sort=[{"property": "teamStats.avgShotsPerGame", "direction": "DESC"}]' +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName' +
        '&include=teamStats.gamesPlayed' +
        '&include=teamStats.avgShotsPerGame' +
        '&include=teamStats.avgShotsAgainstPerGame' +
        '&include=period1Shots' +
        '&include=period2Shots' +
        '&include=period3Shots' +
        '&include=overtimeShots' +
        '&include=shotsFor' +
        '&include=period1ShotsAgainst' +
        '&include=period2ShotsAgainst' +
        '&include=period3ShotsAgainst' +
        '&include=overtimeShotsAgainst' +
        '&include=shotsAgainst',
        args
    );
};

const fetchTeamPostsCrossbarsReport = (args) => {
    return doDefaultFetch('/site/api/team/team-season-shot-attempt' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        '&sort=[{"property": "postPlusCrossbar", "direction": "DESC"}]' +
        '&include=gamesPlayed' +
        '&include=hitCrossbar' +
        '&include=hitPost' +
        '&include=postPlusCrossbar' +
        '&include=shotAttempts' +
        '&include=team.id' +
        '&include=team.placeName' +
        '&include=team.commonName' +
        '&include=team.triCode' +
        '&include=team.logos' +
        '&include=team.fullName',
        args
    );
};

const fetchTeamShootoutsReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}team.league.abbreviation = 'NHL'`

    return doDefaultFetch('/site/api/team/stats' +
        `?cayenneExp=${cayenneExp}` +
        '&sort=[{"property": "shootoutTeamSummary.shootoutGamesWon", "direction": "DESC"}, {"property": "shootoutTeamSummary.shootoutGamesLost", "direction": "ASC"}]' +
        '&include=[' +
            '"id",' +
            '{"team":[' +
                '"id",' +
                '"placeName",' +
                '"commonName",' +
                '"triCode",' +
                '"logos",' +
                '"fullName"' +
            ']},' +
            '{"shootoutTeamSummary":[' +
                '"shootoutGamesPlayed",' +
                '"homeShootoutGamesWon",' +
                '"homeShootoutGamesLost",' +
                '"homeShootoutGoals",' +
                '"homeShotsTaken",' +
                '"homeShootingPctg",' +
                '"homeShotsAgainst",' +
                '"homeGoalsAllowed",' +
                '"homeSavePctg",' +
                '"roadShootoutGamesWon",' +
                '"roadShootoutGamesLost",' +
                '"roadShootoutGoals",' +
                '"roadShotsTaken",' +
                '"roadShootingPctg",' +
                '"roadShotsAgainst",' +
                '"roadGoalsAllowed",' +
                '"roadSavePctg",' +
                '"shootoutGamesWon",' +
                '"shootoutGamesLost",' +
                '"shootoutGoals",' +
                '"shotsTaken",' +
                '"shootingPctg",' +
                '"shotsAgainst",' +
                '"goalsAllowed",' +
                '"savePctg"' +
            ']}' +
        ']',
        args
    );
};

const fetchGoalieCareerCareerLeadersReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}gamesPlayed > 0`;

    return doDefaultFetch('/site/api/goalie/careerTotals' +
        `?cayenneExp=${cayenneExp}` +
        getSortExpression(args.sortInfo, 'player.currentTeam') +
        '&include=player.currentTeam.triCode' +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=firstSeasonForGameType.id' +
        '&include=lastSeasonForGameType.id' +
        '&include=gamesPlayed' +
        '&include=wins' +
        '&include=losses' +
        '&include=ties' +
        '&include=overtimeLosses' +
        '&include=shotsAgainst' +
        '&include=goalsAgainst' +
        '&include=goalsAgainstAverage' +
        '&include=saves' +
        '&include=savePctg' +
        '&include=shutouts' +
        '&include=timeOnIce' +
        getPaginationExpression(args.start, args.limit),
        args
    );
};

const fetchGoalieCareerCareerLeadersForFranchiseReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}gamesPlayed > 0`;

    const includeJson = [
        {
            player: [
                ...playerIncludes,
            ],
        },
        {
            franchise: [
                {
                    franchiseTeams: [
                        {
                            team: [...teamIncludes],
                        },
                        'firstSeason.id',
                        'lastSeason.id',
                    ],
                },
                'mostRecentTeam.triCode',
            ],
        },
        'firstSeasonForGameType.id',
        'lastSeasonForGameType.id',
        'gamesPlayed',
        'wins',
        'losses',
        'ties',
        'overtimeLosses',
        'shotsAgainst',
        'goalsAgainst',
        'goalsAgainstAverage',
        'saves',
        'savePctg',
        'shutouts',
        'timeOnIce',
    ];

    return doDefaultFetch('/site/api/goalie/careerTotalsByFranchise' +
        `?cayenneExp=${cayenneExp}` +
        getSortExpression(args.sortInfo) +
       `&include=${JSON.stringify(includeJson)}` +
        getPaginationExpression(args.start, args.limit),
        args
    );
};

const fetchGoalieCareerSingleSeasonLeadersReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}gamesPlayed > 0`;

    return doDefaultFetch('/site/api/goalie/totals' +
        `?cayenneExp=${cayenneExp}` +
        getSortExpression(args.sortInfo) +
        '&include=goalieStats.team.triCode' +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=season.id' +
        '&include=gamesPlayed' +
        '&include=wins' +
        '&include=losses' +
        '&include=ties' +
        '&include=overtimeLosses' +
        '&include=shotsAgainst' +
        '&include=goalsAgainst' +
        '&include=goalsAgainstAverage' +
        '&include=saves' +
        '&include=savePercentage' +
        '&include=shutouts' +
        '&include=timeOnIce' +
        getPaginationExpression(args.start, args.limit),
        args
    );
};

const fetchGoalieCareerSingleSeasonLeadersForFranchiseReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}gamesPlayed > 0`;

    return doDefaultFetch('/site/api/goalie/stats' +
        `?cayenneExp=${cayenneExp}` +
        getSortExpression(args.sortInfo) +
        '&include=team.triCode' +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=season.id' +
        '&include=gamesPlayed' +
        '&include=wins' +
        '&include=losses' +
        '&include=ties' +
        '&include=overtimeLosses' +
        '&include=shotsAgainst' +
        '&include=goalsAgainst' +
        '&include=goalsAgainstAverage' +
        '&include=saves' +
        '&include=savePercentage' +
        '&include=shutouts' +
        '&include=timeOnIce' +
        getPaginationExpression(args.start, args.limit),
        args
    );
};

const fetchGoalieCareerCareerPenaltyShotsReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}penaltyShotsAgainst > 0`;

    return doDefaultFetch('/site/api/goalie/careerTotals' +
        `?cayenneExp=${cayenneExp}` +
        getSortExpression(args.sortInfo) +
        '&include=player.currentTeam.triCode' +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.position.positionCode' +
        '&include=penaltyShotGoalsAgainst' +
        '&include=penaltyShotSaves' +
        '&include=penaltyShotsAgainst' +
        '&include=penaltyShotSavePctg' +
        getPaginationExpression(args.start, args.limit),
        args
    );
};

const fetchGoalieCareerCareerShootoutLeadersReport = args => {
    let cayenneExp = generateCayenneExp(args, false, IS_ROOKIE_GOALIE_STATS);
    cayenneExp += `${possibleAnd(cayenneExp)}shootoutGamesPlayed > 0`;

    return doDefaultFetch(`/site/api/goalie/careerShootoutTotals` +
        `?cayenneExp=${cayenneExp}` +
        getSortExpression(args.sortInfo) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=player.currentTeam.id' +
        '&include=homeShootoutGamesPlayed' +
        '&include=homeShootoutGamesWon' +
        '&include=homeShootoutGamesLost' +
        '&include=homeShotsAgainst' +
        '&include=homeGoalsAllowed' +
        '&include=homeSavePctg' +
        '&include=roadShootoutGamesPlayed' +
        '&include=roadShootoutGamesWon' +
        '&include=roadShootoutGamesLost' +
        '&include=roadShotsAgainst' +
        '&include=roadGoalsAllowed' +
        '&include=roadSavePctg' +
        '&include=shootoutGamesPlayed' +
        '&include=shootoutGamesWon' +
        '&include=shootoutGamesLost' +
        '&include=shotsAgainst' +
        '&include=goalsAllowed' +
        '&include=savePctg' +
        getPaginationExpression(args.start, args.limit),
        args
    );
};

const fetchGoalieCareerCareerOvertimeReport = args => {
    let cayenneExp = generateCayenneExp(args).replace('gameType', 'gameTypeId');
    cayenneExp += `${possibleAnd(cayenneExp)}gamesPlayed > 0`;

    return doDefaultFetch('/site/api/goalie_career_overtime_totals' +
        `?cayenneExp=${cayenneExp}` +
        getSortExpression(args.sortInfo) +
        '&include=player.firstName' +
        '&include=player.lastName' +
        '&include=player.id' +
        '&include=gamesPlayed' +
        '&include=overtimeWins' +
        '&include=overtimeLosses' +
        '&include=shootoutWins' +
        '&include=shootoutLosses' +
        '&include=shotsAgainst' +
        '&include=goalsAgainst' +
        '&include=savePctg' +
        '&include=timeOnIceMinSec' +
        '&include=activePlayer' +
        getPaginationExpression(args.start, args.limit),
        args
    );
};

const fetchGameSummaryReport = args => {
    return doDefaultFetch('/site/api/game' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        '&sort=[{"property": "gameDate", "direction": "DESC"}]' +
        '&include=id' +
        '&include=gameDate' +
        '&include=homeTeam.triCode' +
        '&include=homeScore' +
        '&include=visitingTeam.triCode' +
        '&include=visitingScore' +
        '&include=overtime' +
        '&include=winningGoalie.firstName' +
        '&include=winningGoalie.lastName' +
        '&include=winningGoalie.id' +
        '&include=winTieGoalScorer.firstName' +
        '&include=winTieGoalScorer.lastName' +
        '&include=winTieGoalScorer.id' +
        '&include=homeTeamStats.shots' +
        '&include=homeTeamStats.powerplayGoalsFor' +
        '&include=homeTeamStats.powerplayOpportunities' +
        '&include=homeTeamStats.penaltyInMinutes' +
        '&include=visitingTeamStats.shots' +
        '&include=visitingTeamStats.powerplayGoalsFor' +
        '&include=visitingTeamStats.powerplayOpportunities' +
        '&include=visitingTeamStats.penaltyInMinutes' +
        '&include=attendance',
        args
    );
};

const fetchGameGameByGameReport = args => {
    return doDefaultFetch('/site/api/team/gameStats' +
        `?cayenneExp=${generateCayenneExp(args, true)}` +
        '&sort=[{"property": "game.gameDate", "direction": "DESC"}]' +
        '&include=game.id' +
        '&include=game.gameDate' +
        '&include=game.overtime' +
        '&include=game.winningGoalie.firstName' +
        '&include=game.winningGoalie.lastName' +
        '&include=game.winningGoalie.id' +
        '&include=game.winTieGoalScorer.firstName' +
        '&include=game.winTieGoalScorer.lastName' +
        '&include=game.winTieGoalScorer.id' +
        '&include=game.attendance' +
        '&include=game.homeTeam.triCode' +
        '&include=game.homeTeamGameNumber' +
        '&include=game.visitingTeamGameNumber' +
        '&include=team.triCode' +
        '&include=decision' +
        '&include=homeRoad' +
        '&include=opponentTeam.triCode' +
        '&include=wins' +
        '&include=losses' +
        '&include=ties' +
        '&include=overtimeLosses' +
        '&include=goalsFor' +
        '&include=goalsAgainst' +
        '&include=scorersBySweaterNumber' +
        '&include=powerplayGoalsFor' +
        '&include=powerplayOpportunities' +
        '&include=powerplayGoalsAgainst' +
        '&include=timesShorthanded' +
        '&include=shots' +
        '&include=shotsAgainst',
        args
    );
};

const fetchGameOvertimeAndShootoutGamesReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}overtime in ('OT', 'SO')`;

    return doDefaultFetch('/site/api/game' +
        `?cayenneExp=${cayenneExp}` +
        '&sort=[{"property": "gameDate", "direction": "DESC"}]' +
        '&include=id' +
        '&include=gameType.id' +
        '&include=gameDate' +
        '&include=winTieGoalScorer.firstName' +
        '&include=winTieGoalScorer.lastName' +
        '&include=winTieGoalScorer.id' +
        '&include=period' +
        '&include=winTieGoalTime' +
        '&include=visitingTeam.triCode' +
        '&include=visitingTeamStats.decision' +
        '&include=visitingScore' +
        '&include=homeTeam.triCode' +
        '&include=homeTeamStats.decision' +
        '&include=homeScore' +
        '&include=overtime' +
        '&include=winningGoalie.firstName' +
        '&include=winningGoalie.lastName' +
        '&include=winningGoalie.id' +
        '&include=losingGoalie.firstName' +
        '&include=losingGoalie.lastName' +
        '&include=losingGoalie.id',
        args
    );
};

const fetchGamePenaltiesByGameReport = args => {
    return doDefaultFetch('/site/api/team/gameStats' +
        `?cayenneExp=${generateCayenneExp(args, true)}` +
        '&sort=[{"property": "game.gameDate", "direction": "DESC"}]' +
        '&include=game.id' +
        '&include=game.gameDate' +
        '&include=team.triCode' +
        '&include=opponentTeam.triCode' +
        '&include=homeRoad' +
        '&include=minors' +
        '&include=majors' +
        '&include=misconducts' +
        '&include=gameMisconducts' +
        '&include=matchPenalties' +
        '&include=benchMinors' +
        '&include=totalPenalties' +
        '&include=penaltyInMinutes',
        args
    );
};

const fetchLeaguePenaltyShotsReport = args => {
    let apiPath = '/site/api/league/seasonStats';
    const includeJson = [
        'season.id',
        'gamesPlayed',
        'penaltyShotAttempts',
        'penaltyShotGoals',
        'penaltyShotGoalPercentage',
    ];

    if (args.goalieFranchise !== 'all' || args.shooterFranchise !== 'all') {
        apiPath = '/site/api/franchiseSeasonPenaltyShots';
        includeJson.push('goalsAgainst');
        includeJson.push('attemptsAgainst');
        includeJson.push('savePercentage');
    }

    return doDefaultFetch(apiPath +
        `?cayenneExp=${generateCayenneExp(args)}` +
        '&sort=[{"property": "season.id", "direction": "DESC"}]' +
        `&include=${JSON.stringify(includeJson)}`,
        args
    );
}

const fetchMiscellaneousPenaltyShotsReport = args => {
    const includes = [{
        game: [
            'id',
            'gameDate',
            'homeScore',
            'visitingScore',
            'gameFormat',
            {homeTeam: [...teamIncludes]},
            {visitingTeam: [...teamIncludes]}
        ]
    }, {
        shooter: [...playerIncludes],
    }, {
        shooterTeam: [...teamIncludes],
    }, {
        goalie: [...playerIncludes],
    }, {
        goalieTeam: [...teamIncludes],
    }, 'didScore', 'shotTime', 'shotPeriod'];

    return doDefaultFetch('/site/api/game/penaltyShots' +
        `?cayenneExp=${generateCayenneExp(args, true)}` +
        '&sort=[{"property": "game.gameDate", "direction": "DESC"}]' +
        `&include=${JSON.stringify(includes)}`,
        args
    );
};

const fetchGameShutoutsReport = args => {
    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}(homeScore = 0 or visitingScore = 0) and state >= ${gameStateTypes.SOFT_FINAL}`;

    return doDefaultFetch('/site/api/game' +
        `?cayenneExp=${cayenneExp}` +
        '&sort=[{"property": "gameDate", "direction": "DESC"}]' +
        '&include=id' +
        '&include=gameDate' +
        '&include=overtime' +
        '&include=visitingTeam.triCode' +
        '&include=visitingScore' +
        '&include=visitingTeamStats.decision' +
        '&include=homeTeam.triCode' +
        '&include=homeScore' +
        '&include=homeTeamStats.decision' +
        '&include=winningGoalie.firstName' +
        '&include=winningGoalie.lastName' +
        '&include=winningGoalie.id' +
        '&include=losingGoalie.firstName' +
        '&include=losingGoalie.lastName' +
        '&include=losingGoalie.id' +
        '&include=attendance',
        args
    );
};

const fetchLeagueComebackWinsReport = args => {
    const includeJson = [
        'season.id',
    ];

    return doDefaultFetch('/site/api/league_comeback_wins' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        '&sort=[{"property": "season.id", "direction": "DESC"}]' +
        `&include=${JSON.stringify(includeJson)}`,
        args
    );
}

const fetchLeagueGoalsReport = args => {
    const includeJson = [
        'season.id',
        'gamesPlayed',
        'goals',
        'goalsPerGame',
        'ppGoals',
        'ppOpportunities',
        'ppPctg',
        'shGoals',
        'penaltyShotGoals',
        'emptyNetGoals',
    ];

    return doDefaultFetch('/site/api/league/seasonStats' +
        `?cayenneExp=${generateCayenneExp(args)}` +
        '&sort=[{"property": "season.id", "direction": "DESC"}]' +
        `&include=${JSON.stringify(includeJson)}`,
        args
    );
}

const fetchLeagueGamesDecidedInOtSoReport = args => {
    const includeJson = [
        'season.id',
        'gamesPlayed',
        'gamesPastRegulation',
        'gamesWonInOt',
        'gamesWonInShootout',
        'pctWonInOvertime',
    ];

    let cayenneExp = generateCayenneExp(args);
    cayenneExp += `${possibleAnd(cayenneExp)}gameType = ${constants.REGULAR_SEASON} and season >= 20052006`;

    return doDefaultFetch('/site/api/league/seasonStats' +
        `?cayenneExp=${cayenneExp}` +
        '&sort=[{"property": "season.id", "direction": "DESC"}]' +
        `&include=${JSON.stringify(includeJson)}`,
        args
    );
}

const fetchHeadCoachCareerReport = (args) => {
    const endpoint = args.gameType === constants.REGULAR_SEASON ? 'franchiseRegularSeason' : 'franchise';
    const includeJson = [
        'activeCoach',
        'coachId',
        'coachName',
        'lastName',
        'firstName',
        'stanleyCups',
        'games',
        'wins',
        'losses',
        'ties',
        'otLosses',
        'pointPctg',
        'winPctg',
        'teamName',
        'startSeason.id',
        'endSeason.id',
        {
            franchise: franchiseTeamIncludes,
        },
    ];

    return doDefaultFetch(`/site/api/coach/franchiseRecords` +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&include=${JSON.stringify(includeJson)}`,
        args
    );
};

const fetchFranchiseAllTimeGame7Report = args => {
    const includeJson = [
        {
            franchise: [
                'id',
                'fullName',
                ...franchiseTeamIncludes,
            ]
        },
    ];
    const sortJson = [{
        property: 'winPctg',
        direction: 'DESC',
    }];

    return doDefaultFetch(`/site/api/franchise/allTimeGame7Record` +
        `?sort=${JSON.stringify(sortJson)}` +
        `&include=${JSON.stringify(includeJson)}`,
        args
    );
};

const fetchFranchiseAllTimeOvertimeReport = args => {
    const includeJson = [
        {
            franchise: [
                'id',
                'fullName',
                ...franchiseTeamIncludes,
            ]
        },
    ];
    const sortJson = [{
        property: 'winPctg',
        direction: 'DESC',
    }];

    return doDefaultFetch(`/site/api/franchise/allTimeOvertimeRecord` +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${JSON.stringify(sortJson)}` +
        `&include=${JSON.stringify(includeJson)}`,
        args
    );
};

const fetchFranchiseAllTimeStandingsReport = args => {
    const includeJson = [{
            franchise: [
                'id',
                'fullName',
                ...franchiseTeamIncludes,
            ]
        }, {
            firstSeason: [
                'id',
                'tiesInUse',
                'pointForOTLossInUse',
            ]
        }, {
            lastSeason: [
                'id',
                'tiesInUse',
                'pointForOTLossInUse',
            ]
        }
    ];
    const sortJson = [{
        property: 'firstSeason.id',
        direction: 'DESC',
    }];


    return doDefaultFetch(`/site/api/franchise/teamTotals?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${JSON.stringify(sortJson)}` +
        `&include=${JSON.stringify(includeJson)}`,
        args
    );
};

const fetchFranchiseAllTimePenaltyShotsReport = args => {
    const includeJson = [
        {
            franchise: [
                'id',
                'fullName',
                ...franchiseTeamIncludes,
            ]
        },
        'lastSeason.id',
        'firstSeason.id',
    ];
    const sortJson = [{
        property: 'shootingPctg',
        direction: 'DESC',
    }];

    return doDefaultFetch(`/site/api/franchise/allTimePenaltyShotRecord` +
        `?cayenneExp=${generateCayenneExp(args)}` +
        `&sort=${JSON.stringify(sortJson)}` +
        `&include=${JSON.stringify(includeJson)}`,
        args
    );
};

const playerIncludes = [
    'firstName',
    'lastName',
    'id',
];

const teamIncludes = [
    'id',
    'placeName',
    'commonName',
    'triCode',
    'logos',
    'fullName',
];

const franchiseTeamIncludes = [{ teams: [...teamIncludes, 'active'] }];

const getPlayerTeamIncludes = args => args.team.id === 'all' ? '&include=playerStats.team.triCode&include=playerStats.team.fullName' : '&include=team.triCode&include=team.fullName';

const getGoalieTeamIncludes = args => args.team.id === 'all' ? '&include=goalieStats.team.triCode&include=goalieStats.team.fullName' : '&include=team.triCode&include=team.fullName';

const statsOrTotals = args => args.team.id === 'all' ? 'totals' : 'stats';

const summaryOrTotals = args => args.team.id === 'all' ? 'Totals' : 'Summary';

const generateCayenneExp = (args, getGameDateFromGame, rookieStatsSource) => {
    const { filterFlags } = args;
    let cayenneExp = '';

    if (filterFlags.showSeasons) {
        cayenneExp += possibleAnd(cayenneExp) + `season=${args.season.id}`;
    }

    if (runFunctionOrGetValue(filterFlags.showGameType, args.season?.id)) {
        cayenneExp += possibleAnd(cayenneExp) + `gameType=${args.gameType}`;
    }

    if (filterFlags.showTeam) {
        cayenneExp += args.team.id !== 'all' ? `${possibleAnd(cayenneExp)}team=${args.team.id}` : '';
    }

    if (filterFlags.showFranchise) {
        cayenneExp += args.franchise !== 'all' ? `${possibleAnd(cayenneExp)}franchise=${args.franchise}` : '';
    }

    if (filterFlags.showGoalieFranchise || filterFlags.showShooterFranchise) {
        if (args.goalieFranchise !== 'all' || args.shooterFranchise !== 'all') {
            cayenneExp += `${possibleAnd(cayenneExp)}franchiseId=${args.shooterFranchise === 'all' ? 'null' : args.shooterFranchise}`;
            cayenneExp += `${possibleAnd(cayenneExp)}goalieFranchiseId=${args.goalieFranchise === 'all' ? 'null' : args.goalieFranchise}`;
        }
    }

    if (filterFlags.showPosition) {
        if (args.position !== 'all') {
            cayenneExp += possibleAnd(cayenneExp) + 'player.position.positionCode in (';

            switch(args.position) {
                case 's':
                    cayenneExp += '"C","D","F","L","R"';
                    break;
                case 'f':
                    cayenneExp += '"C","F","L","R"';
                    break;
                case 'l':
                    cayenneExp += '"L"';
                    break;
                case 'r':
                    cayenneExp += '"R"';
                    break;
                case 'c':
                    cayenneExp += '"C"';
                    break;
                case 'd':
                    cayenneExp += '"D"';
                    break;
            }

            cayenneExp += ')';
        }
    }

    if (filterFlags.showStatus) {
        // '' for IS_ROOKIE_STATS
        let topLevelObj = '';
        if (rookieStatsSource === IS_ROOKIE_PLAYER_STATS) {
            topLevelObj = 'playerStats.';
        } else if (rookieStatsSource === IS_ROOKIE_GOALIE_STATS) {
            topLevelObj = 'goalieStats.';
        }

        cayenneExp += args.status !== 'all'
            ? possibleAnd(cayenneExp) +
                (
                    args.status === 'r'
                        ? `${topLevelObj}isRookie="Y"`
                        : `(${topLevelObj}isRookie!="Y" or ${topLevelObj}isRookie = null)`
                )
            : '';
    }

    if (filterFlags.showCountry) {
        cayenneExp += args.country !== 'all' ? `${possibleAnd(cayenneExp)}player.birthCountry="${args.country}"` : '';
    }

    if (filterFlags.showDateRange) {
        cayenneExp += args.fromDate ? `${possibleAnd(cayenneExp)}${getGameDateFromGame ? 'game.' : ''}gameDate >= "${args.fromDate.format(constants.DATE_FORMAT)}"` : '';
        cayenneExp += args.toDate ? `${possibleAnd(cayenneExp)}${getGameDateFromGame ? 'game.' : ''}gameDate < "${args.toDate.add(1, 'day').format(constants.DATE_FORMAT)}"` : '';
    }

    if (filterFlags.showPlayerSearch && args.player !== null) {
        cayenneExp += args.player ? `${possibleAnd(cayenneExp)}player=${args.player}` : '';
    }

    if (args.category === 'league') {
        cayenneExp += `${possibleAnd(cayenneExp)}gamesPlayed > 0`
    }

    return cayenneExp;
};

const generateTeamSort = (sorting) => {
    const filteredSort = sorting.filter((item) => item.id !== 'team.placeName');
    return generateSort(filteredSort);
}

const generateSort = (sorting) => {
    const sortList = sorting.map((item) => ({
        property: item.id,
        direction: item.desc ? 'DESC' : 'ASC'
    }));
    return JSON.stringify(sortList);
};

const possibleAnd = exp => exp === '' ? '' : ' and ';

const getPaginationExpression = (start, limit) => `&start=${start}&limit=${limit}`;

const getSortExpression = (sortInfo = [], teamAccessor = 'team') => sortInfo.length > 0  ? `&sort=[${sortInfo.reduce((acc, sortData) => {
    let sortObjString = '';
    
    if (sortData.id !== 'rank') {
        let property = sortData.id;
        let secondarySort;
        
        if (acc) {
            sortObjString += acc + ',';
        }
        
        // certain sorts will require additional logic to handle correctly - somtimes that's just making sure
        // the name matches up with the real thing we're sorting on. Other times that means sorting on a secondary
        // thing to make sure we get consistent results (eg, for goalie save percentages, if a goalie has 0 shots against
        // (and, by definition a 0% save percentage) we want them below a goalie with > 0 shots against and a 0% save percentage)
        if (sortData.id === 'team') {
            property = `${teamAccessor}.fullName`;
        } else if (sortData.id === 'player') {
            property = 'player.lastName';
            secondarySort = `{"property": "player.firstName", "direction": "${sortData.desc ? 'DESC' : 'ASC'}"}`;
            
            return sortObjString;
        } else if (sortData.id.toLowerCase().includes('savepctg')) {
            let idPrefix = sortData.id.substring(0, sortData.id.toLowerCase().indexOf('savepctg'));
            
            if (idPrefix.toLowerCase().includes('shot')) {
                idPrefix = idPrefix.substring(0, idPrefix.toLowerCase().indexOf('shot'));
            }
            
            secondarySort = `{"property": "${idPrefix ? `${idPrefix}S` : 's'}hotsAgainst", "direction": "${sortData.desc ? 'DESC' : 'ASC'}"}`;
        } else if (sortData.id.toLowerCase().includes('shootingpctg')) {
            let idPrefix = sortData.id.substring(0, sortData.id.toLowerCase().indexOf('shootingpctg'));

            if (idPrefix.toLowerCase().includes('shot')) {
                idPrefix = idPrefix.substring(0, idPrefix.toLowerCase().indexOf('shot'));
            }
            
            secondarySort = `{"property": "${idPrefix ? `${idPrefix}S` : 's'}hotsTaken", "direction": "${sortData.desc ? 'DESC' : 'ASC'}"}`;
        }

        sortObjString += `{"property": "${property}", "direction": "${sortData.desc ? 'DESC' : 'ASC'}"}${secondarySort ? `,${secondarySort}` : ''}`;
    }
    
    return sortObjString;
}, '')}]` : '';

export default {
    fetchPlayerCountries,
    fetchFranchises,
    fetchTeamSummaryReport,
    fetchTeamGoalsHomeRoadReport,
    fetchTeamGoalsByPeriodReport,
    fetchTeamGoalsForReport,
    fetchTeamGoalsAgainstReport,
    fetchTeamPlusMinusReport,
    fetchSkaterSummaryReport,
    fetchSkaterAssistsReport,
    fetchSkaterBiosReport,
    fetchSkaterDivisionReport,
    fetchSkaterFaceoffLeadersReport,
    fetchSkaterFaceoffsReport,
    fetchSkaterGoalsReport,
    fetchSkaterHomeReport,
    fetchSkaterPenaltiesReport,
    fetchSkaterPlusMinusReport,
    fetchSkaterPointsReport,
    fetchSkaterRoadReport,
    fetchSkaterShootingPercentageLeadersReport,
    fetchSkaterShootingPercentageReport,
    fetchSkaterShootoutReport,
    fetchSkaterPointsByStrengthReport,
    fetchSkaterTimeOnIceReport,
    fetchSkaterRealTimeReport,
    fetchSkaterCareerCareerLeadersReport,
    fetchSkaterCareerSingleSeasonLeadersReport,
    fetchSkaterCareerCareerLeadersForFranchiseReport,
    fetchSkaterCareerSingleSeasonLeadersForFranchiseReport,
    fetchSkaterCareerCareerPenaltyShotsReport,
    fetchSkaterCareerCareerShootoutLeadersReport,
    fetchSkaterCareerCareerOvertimeReport,
    fetchGoalieSummaryReport,
    fetchGoalieBiosReport,
    fetchGoalieGoalsAgainstAverageLeadersReport,
    fetchGoalieGoalsAgainstAverageReport,
    fetchGoalieHomeReport,
    fetchGoaliePenaltyShotsReport,
    fetchGoalieRoadReport,
    fetchGoalieSavePercentageLeadersReport,
    fetchGoalieSavePercentageReport,
    fetchGoalieShootoutsReport,
    fetchGoalieSpecialTeamsReport,
    fetchTeamComebackWinsReport,
    fetchTeamTimeLeadingTrailingReport,
    fetchTeamGamesDecidedInOtSoReport,
    fetchTeamGoalGamesReport,
    fetchTeamHomeReport,
    fetchTeamOutshootOutshotByReport,
    fetchTeamPenaltiesReport,
    fetchTeamPostsCrossbarsReport,
    fetchTeamPowerplayReport,
    fetchTeamPowerplayTimeReport,
    fetchTeamPenaltyKillReport,
    fetchTeamPenaltyKillTimeReport,
    fetchTeamRealTimeReport,
    fetchTeamRecordsInBackToBackGamesReport,
    fetchTeamRoadReport,
    fetchTeamLeadingTrailingReport,
    fetchTeamSavePercentageReport,
    fetchTeamScoreTrailFirstReport,
    fetchTeamScoringByDefensemenReport,
    fetchTeamScoringByForwardsReport,
    fetchTeamScoringByGoaliesReport,
    fetchTeamScoringByRookiesReport,
    fetchTeamShotsByPeriodReport,
    fetchTeamShootoutsReport,
    fetchGoalieCareerCareerLeadersReport,
    fetchGoalieCareerCareerLeadersForFranchiseReport,
    fetchGoalieCareerSingleSeasonLeadersReport,
    fetchGoalieCareerSingleSeasonLeadersForFranchiseReport,
    fetchGoalieCareerCareerPenaltyShotsReport,
    fetchGoalieCareerCareerShootoutLeadersReport,
    fetchGoalieCareerCareerOvertimeReport,
    fetchGameSummaryReport,
    fetchGameGameByGameReport,
    fetchGameOvertimeAndShootoutGamesReport,
    fetchGamePenaltiesByGameReport,
    fetchGameShutoutsReport,
    fetchMiscellaneousPenaltyShotsReport,
    fetchLeagueComebackWinsReport,
    fetchLeaguePenaltyShotsReport,
    fetchLeagueGamesDecidedInOtSoReport,
    fetchLeagueGoalsReport,
    fetchHeadCoachCareerReport,
    fetchFranchiseAllTimeGame7Report,
    fetchFranchiseAllTimeOvertimeReport,
    fetchFranchiseAllTimePenaltyShotsReport,
    fetchFranchiseAllTimeStandingsReport,
};
